// Main top-down arena. The hero hunts the room: search hotspots to expose
// hidden assets, then patch them before bouncing CVEs trigger an
// 'EXPLOITING' timer. Hotspots reshuffle every roundResetSec. Boss on
// patched-count hitting target or main timer expiring.

import Phaser from 'phaser';
import { brand, brandHex } from '../brand';
import { config } from '../config';
import { GameParams } from '../params';
import { buildArena } from '../systems/arena';
import { AssetSystem } from '../systems/assets';
import { CveSystem } from '../systems/cves';
import { createHud, HudHandles, updateAssetChip } from '../systems/hud';
import { createInput, InputAccess } from '../systems/input';
import { SPRITE_KEYS } from '../systems/sprites';
import { createRunState, RunState } from '../systems/state';
import { track } from '../systems/analytics';
import { AUDIO_KEYS, playBgm, playSfx } from '../systems/audio';

export class GameScene extends Phaser.Scene {
  private params!: GameParams;
  private state!: RunState;
  private hud!: HudHandles;
  private controls!: InputAccess;
  private player!: Phaser.Physics.Arcade.Sprite;
  private dashUntil = 0;
  private dashCooldownUntil = 0;
  private iframesUntil = 0;
  private assets!: AssetSystem;
  private cves!: CveSystem;
  private bounds!: Phaser.Geom.Rectangle;
  private countdownStart = 0;
  private roundResetTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super('Game');
  }

  init(data: { params: GameParams }) {
    this.params = data.params;
    this.state = createRunState(this.params, config.assets.count, config.player.healthSegments);

    // Reset run-specific instance fields. Phaser reuses the Scene instance
    // across scene.start() calls so class-field defaults don't re-run.
    this.dashUntil = 0;
    this.dashCooldownUntil = 0;
    this.iframesUntil = 0;
    this.countdownStart = 0;
    this.roundResetTimer = undefined;
  }

  create() {
    this.cameras.main.setBackgroundColor(brand.bg);

    const arena = buildArena(this);
    this.bounds = arena.bounds;

    // player
    this.player = this.physics.add.sprite(
      this.bounds.x + 80,
      this.bounds.y + this.bounds.height / 2,
      SPRITE_KEYS.playerBody,
    );
    this.player.setDisplaySize(40, 40);
    (this.player.body as Phaser.Physics.Arcade.Body).setCircle(14, 6, 12);
    this.player.setDepth(10);
    this.player.setCollideWorldBounds(false);
    this.physics.add.collider(this.player, arena.walls);
    this.physics.add.collider(this.player, arena.items);

    // shadow
    const shadow = this.add.ellipse(this.player.x, this.player.y + 14, 30, 8, 0x000000, 0.45).setDepth(9);
    this.events.on(Phaser.Scenes.Events.UPDATE, () => {
      shadow.setPosition(this.player.x, this.player.y + 14);
    });

    // HUD
    this.hud = createHud(this, {
      name: this.params.name,
      total: config.assets.count,
      healthMax: config.player.healthSegments,
    });
    this.hud.setHealth(this.state.health, config.player.healthSegments);
    this.hud.setCountdown(config.timers.countdownSec);
    // 0 patched, total = 5 → chip row reads "EXPOSED 0/5"
    this.hud.setAssets(config.assets.count, config.assets.count);

    // input
    this.controls = createInput(this);

    // assets
    this.assets = new AssetSystem(this, this.bounds, {
      onSearch: (kind) => track('asset_searched', { result: 'asset', kind }),
      onCarryStart: () => this.player.setTexture(SPRITE_KEYS.playerCarry),
      onCarryEnd: () => this.player.setTexture(SPRITE_KEYS.playerBody),
      onPatch: (type, slotIndex) => {
        this.state.patchedCount++;
        track('asset_patched', { type });
        const remaining = config.assets.count - this.assets.getPatchedCount();
        this.hud.setAssets(remaining, config.assets.count);
        // Fill the corresponding HUD chip with the asset sprite.
        updateAssetChip(slotIndex, type, 'patched');
      },
      onExploited: (type) => {
        track('asset_exploited', { type });
        this.applyDamage();
      },
      onAllPatched: () => {
        track('all_patched', { elapsedMs: this.time.now - this.countdownStart });
        this.toBoss();
      },
      // CVE just kicked off an in-place exploit — pause the threat layer so
      // the player can focus on defending, and pulse a red glow around the
      // play frame as the warning cue (replacing the old alarm SFX).
      onExploitStart: () => {
        this.cves.pauseSpawning();
        document.getElementById('exploit-warning')?.classList.remove('hidden');
      },
      onExploitEnd: () => {
        this.cves.resumeSpawning();
        document.getElementById('exploit-warning')?.classList.add('hidden');
      },
    });
    this.assets.init(arena.hotspots, arena.scanner);

    // CVEs — bounce off walls and items via Phaser collisions, exploit on contact
    this.cves = new CveSystem(this, this.bounds);
    this.cves.start();
    this.physics.add.collider(this.cves.getGroup(), arena.walls);
    this.physics.add.collider(
      this.cves.getGroup(),
      arena.items,
      (_cve, item) => this.assets.handleCveHit(item as Phaser.GameObjects.GameObject),
    );
    this.physics.add.overlap(this.player, this.cves.getGroup(), (_, cve) =>
      this.onCveHit(cve as Phaser.Physics.Arcade.Sprite),
    );

    // Round reset every N seconds — reshuffles dormant/hidden/exposed
    // hotspots; patched/exploited stay terminal.
    this.roundResetTimer = this.time.addEvent({
      delay: config.assets.roundResetSec * 1000,
      loop: true,
      callback: () => {
        if (this.state.outcome !== 'pending' || this.state.reachedBoss) return;
        this.assets.startRound(config.assets.count);
      },
    });

    // intro flourish + start gameplay BGM
    this.hud.showCenterBanner('SEARCH THE ATTACK SURFACE', brandHex.bgDeep, 1400);
    playBgm(this, AUDIO_KEYS.bgmMain);

    // 'CVES INCOMING' warning flashes ~1.4s before the first wave arrives,
    // so the alert ends right as CVEs start flying in.
    this.time.delayedCall(Math.max(0, config.cves.firstSpawnDelayMs - 1400), () => {
      if (this.state.outcome !== 'pending' || this.state.reachedBoss) return;
      this.hud.flashAlert('CVES INCOMING', brandHex.threat, 1400);
    });
    track('game_started', { name: this.params.name, company: this.params.company, total: config.assets.count });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.controls.destroy();
      this.roundResetTimer?.remove();
      document.getElementById('exploit-warning')?.classList.add('hidden');
      if (!this.state.reachedBoss) this.hud.destroy();
    });
  }

  update(_time: number, _delta: number) {
    if (this.countdownStart === 0) {
      this.countdownStart = this.time.now;
      this.state.startTimeMs = this.countdownStart;
    }
    if (this.state.outcome !== 'pending') return;

    // movement
    const speed = this.time.now < this.dashUntil ? config.player.dashSpeed : config.player.speed;
    const ax = this.controls.axisX();
    const ay = this.controls.axisY();
    const len = Math.hypot(ax, ay);
    const nx = len > 0 ? ax / len : 0;
    const ny = len > 0 ? ay / len : 0;
    this.player.setVelocity(nx * speed, ny * speed);

    if (nx < -0.1) this.player.setFlipX(true);
    else if (nx > 0.1) this.player.setFlipX(false);

    // clamp player to arena bounds
    const px = Phaser.Math.Clamp(this.player.x, this.bounds.x + 14, this.bounds.right - 14);
    const py = Phaser.Math.Clamp(this.player.y, this.bounds.y + 14, this.bounds.bottom - 14);
    this.player.setPosition(px, py);

    // ACTION priority:
    //   PATCH (exposed at scanner OR exploiting hotspot)
    //   > SEARCH (hidden hotspot, only when not already carrying)
    //   > DASH.
    // Drop at the scanner is automatic — handled inside AssetSystem.update().
    const isCarrying = this.assets.isCarrying();
    const canPatch =
      this.assets.getExposedAtScanner(this.player) !== null ||
      this.assets.getNearbyExploiting(this.player) !== null;
    const canSearch = !canPatch && !isCarrying && this.assets.getNearbyHidden(this.player) !== null;
    const canDash = this.time.now >= this.dashCooldownUntil;

    if (canPatch) this.controls.setActionLabel('PATCH', brandHex.ok);
    else if (canSearch) this.controls.setActionLabel('SEARCH', brandHex.asset);
    else if (canDash) this.controls.setActionLabel('DASH', brandHex.primary);
    else this.controls.setActionLabel('…', brandHex.textDim);

    if (this.controls.actionPressed()) {
      if (canPatch) {
        this.assets.tryPatch(this.player);
      } else if (canSearch) {
        this.assets.trySearch(this.player);
      } else if (canDash) {
        this.dashUntil = this.time.now + config.player.dashMs;
        this.dashCooldownUntil = this.time.now + config.player.dashCooldownMs;
        this.iframesUntil = this.time.now + config.player.dashMs;
        playSfx(this, AUDIO_KEYS.sfxDash);
        this.add.particles(this.player.x, this.player.y, SPRITE_KEYS.pixel, {
          lifespan: 200,
          quantity: 12,
          speed: { min: 60, max: 140 },
          tint: brandHex.primaryAlt,
          alpha: { start: 0.7, end: 0 },
          scale: { start: 4, end: 0 },
          blendMode: 'ADD',
          emitting: false,
        }).explode(12);
      }
    }

    this.assets.update(this.player);
    this.cves.update();

    // i-frame visual flicker
    if (this.time.now < this.iframesUntil) {
      const t = Math.floor(this.time.now / 80) % 2 === 0;
      this.player.setAlpha(t ? 0.4 : 1);
    } else if (this.player.alpha !== 1) {
      this.player.setAlpha(1);
    }

    // countdown
    const elapsed = (this.time.now - this.countdownStart) / 1000;
    const remaining = Math.max(0, config.timers.countdownSec - elapsed);
    this.hud.setCountdown(Math.ceil(remaining));
    if (remaining <= 0 && !this.state.reachedBoss) {
      // Timer ran out — boss arrives regardless of patch count.
      this.toBoss();
    }
  }

  /** Player is hit by a CVE (direct collision). */
  private onCveHit(cve: Phaser.Physics.Arcade.Sprite) {
    if (this.time.now < this.iframesUntil) return;
    this.applyDamage();

    // little impact burst
    this.add.particles(cve.x, cve.y, SPRITE_KEYS.pixel, {
      lifespan: 350,
      quantity: 14,
      speed: { min: 90, max: 220 },
      tint: brandHex.threat,
      alpha: { start: 0.9, end: 0 },
      scale: { start: 5, end: 0 },
      blendMode: 'ADD',
      emitting: false,
    }).explode(14);

    // remove this CVE
    const emitter = cve.getData('emitter') as Phaser.GameObjects.Particles.ParticleEmitter | undefined;
    emitter?.destroy();
    cve.destroy();
  }

  /** Subtract one health segment from any source (CVE direct hit, exploited asset). */
  private applyDamage() {
    if (this.state.outcome !== 'pending') return;
    if (this.time.now < this.iframesUntil) return;
    this.iframesUntil = this.time.now + config.player.iframeMs;
    this.state.health = Math.max(0, this.state.health - 1);
    this.state.cveHits++;
    this.hud.setHealth(this.state.health, config.player.healthSegments);
    playSfx(this, AUDIO_KEYS.sfxCveHit);
    track('cve_hit', { remainingHealth: this.state.health });
    this.cameras.main.shake(160, 0.005);
    this.player.setTintFill(0xffffff);
    this.time.delayedCall(80, () => this.player.clearTint());
    if (this.state.health <= 0) this.endRun('health');
  }

  private endRun(outcome: 'health' | 'timeout') {
    if (this.state.outcome !== 'pending') return;
    if (this.state.reachedBoss) return;
    this.state.outcome = outcome;
    this.state.endTimeMs = this.time.now;
    this.cves.stop();
    this.hud.flashAlert(outcome === 'health' ? 'COMPROMISED' : 'TIME EXPIRED', brandHex.threat, 1400);
    track(outcome === 'health' ? 'lost_health' : 'lost_timeout', {
      patched: this.state.patchedCount,
      total: this.state.totalAssets,
    });
    this.time.delayedCall(1500, () => this.scene.start('End', { params: this.params, state: this.state }));
  }

  /** Move on to BossScene — either all assets patched or main timer expired. */
  private toBoss() {
    if (this.state.reachedBoss) return;
    this.state.reachedBoss = true;
    this.cves.stop();
    this.iframesUntil = this.time.now + 2000;
    playSfx(this, AUDIO_KEYS.sfxAssetsSecured);
    this.hud.flashAlert('MYTHOS INCOMING', brandHex.threat, 800);
    track('boss_started', { patched: this.assets.getPatchedCount() });
    this.time.delayedCall(900, () => {
      this.cves.destroyAll();
      this.scene.start('Boss', { params: this.params, state: this.state });
    });
  }
}
